/**
 * Mirror of backend/config/sentimentTaxonomy.js (a backend test pins this
 * file textually). Display metadata lives here so every surface - legend,
 * grid headers, drawer chips - reads the same words and colors.
 */
export const EMOTIONS = ["joy", "admiration", "amusement", "surprise", "confusion", "disappointment", "anger", "disgust", "sadness", "fear", "neutral"];
export const POLARITIES = ["positive", "negative", "neutral"];

export const EMOTION_META = {
	joy: { label: "Joy", abbr: "Joy", hint: "Delight, celebration", bg: "bg-emotion-joy", text: "text-emotion-joy", hex: "#FFC44D" },
	admiration: { label: "Admiration", abbr: "Adm", hint: "Praise, being impressed", bg: "bg-emotion-admiration", text: "text-emotion-admiration", hex: "#FF5FA2" },
	amusement: { label: "Amusement", abbr: "Amu", hint: "Laughing with it", bg: "bg-emotion-amusement", text: "text-emotion-amusement", hex: "#19C3B1" },
	surprise: { label: "Surprise", abbr: "Sur", hint: "Didn't see that coming", bg: "bg-emotion-surprise", text: "text-emotion-surprise", hex: "#4CC9F0" },
	confusion: { label: "Confusion", abbr: "Con", hint: "Wait, what?", bg: "bg-emotion-confusion", text: "text-emotion-confusion", hex: "#C084FC" },
	disappointment: { label: "Disappointment", abbr: "Dpt", hint: "Expected better", bg: "bg-emotion-disappointment", text: "text-emotion-disappointment", hex: "#8D99AE" },
	anger: { label: "Anger", abbr: "Ang", hint: "Hostility, outrage", bg: "bg-emotion-anger", text: "text-emotion-anger", hex: "#9D174D" },
	disgust: { label: "Disgust", abbr: "Dis", hint: "Revulsion, contempt", bg: "bg-emotion-disgust", text: "text-emotion-disgust", hex: "#6B8E23" },
	sadness: { label: "Sadness", abbr: "Sad", hint: "Hurt, sympathy", bg: "bg-emotion-sadness", text: "text-emotion-sadness", hex: "#3A6EA5" },
	fear: { label: "Fear", abbr: "Fear", hint: "Worry, dread", bg: "bg-emotion-fear", text: "text-emotion-fear", hex: "#5E548E" },
	neutral: { label: "Neutral", abbr: "Neu", hint: "No clear feeling", bg: "bg-emotion-neutral", text: "text-emotion-neutral", hex: "#CBD5E1" },
};

export const POLARITY_META = {
	positive: { label: "Positive" },
	negative: { label: "Negative" },
	neutral: { label: "Neutral" },
};

/** Ordered series keys for charts: strongest-signal emotions first, neutral last. */
export const CHART_ORDER = ["admiration", "joy", "amusement", "surprise", "confusion", "sadness", "fear", "disappointment", "disgust", "anger", "neutral"];

/**
 * Where a reply stands toward the ACCOUNT, and who its feeling is aimed at.
 * Emotion says what someone feels; stance says whether they are with you or
 * against you; target says at whom. Anger on your side is {anger, for,
 * third_party}. Anger at you is {anger, against, account}. Mirrors
 * backend/config/sentimentTaxonomy.js.
 */
export const STANCES = ["for", "against", "none"];
export const TARGETS = ["account", "third_party", "topic", "none"];

export const STANCE_META = {
	for: { label: "Support", short: "With", hint: "Agrees with, supports, or defends the account", bg: "bg-stance-for", text: "text-stance-for", soft: "bg-stance-for/10 text-stance-for" },
	against: { label: "Opposition", short: "Against", hint: "Disagrees with, criticises, mocks, or attacks the account", bg: "bg-stance-against", text: "text-stance-against", soft: "bg-stance-against/10 text-stance-against" },
	none: { label: "No position", short: "None", hint: "A question, a tag, a link, or a feeling about the situation itself", bg: "bg-input", text: "text-stance-none", soft: "bg-muted text-stance-none" },
};

export const TARGET_META = {
	account: { label: "At the account" },
	third_party: { label: "At someone else" },
	topic: { label: "About the situation" },
	none: { label: "No target" },
};

/**
 * What KIND of trouble a reply carries, beyond whether it is for or against.
 * Stance cannot say "this person is threatening to sue" - signals can. Zero or
 * more per reply; most carry none. Ordered by how urgently a manager needs to
 * see it, which is the order the threat lane renders. Mirrors
 * backend/config/sentimentTaxonomy.js.
 */
export const SIGNALS = ["legal_threat", "allegation", "boycott", "press", "misinformation", "impersonation", "endorsement"];

export const SIGNAL_META = {
	legal_threat: { label: "Legal threat", short: "Legal", hint: "Someone says they will sue, involve lawyers, or call the police", tone: "bad", chip: "bg-rose-100 text-rose-800 dark:bg-rose-500/15 dark:text-rose-300", dot: "bg-rose-600" },
	allegation: { label: "Accusation", short: "Accusation", hint: "An accusation of specific wrongdoing, or a claim to have receipts", tone: "bad", chip: "bg-amber-100 text-amber-900 dark:bg-amber-500/15 dark:text-amber-300", dot: "bg-amber-500" },
	boycott: { label: "Boycott call", short: "Boycott", hint: "Telling others to unfollow, cancel, or stop buying", tone: "bad", chip: "bg-orange-100 text-orange-800 dark:bg-orange-500/15 dark:text-orange-300", dot: "bg-orange-500" },
	misinformation: { label: "False claim", short: "False claim", hint: "Something untrue about you, stated as fact", tone: "bad", chip: "bg-purple-100 text-purple-800 dark:bg-purple-500/15 dark:text-purple-300", dot: "bg-purple-500" },
	impersonation: { label: "Fake account", short: "Fake", hint: "Someone pretending to be you, often a scam", tone: "bad", chip: "bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-500/15 dark:text-fuchsia-300", dot: "bg-fuchsia-500" },
	press: { label: "Press", short: "Press", hint: "A journalist or news outlet, or a link to coverage", tone: "watch", chip: "bg-brand-100 text-brand-800 dark:bg-brand-500/15 dark:text-brand-300", dot: "bg-brand-500" },
	endorsement: { label: "Public backing", short: "Backing", hint: "Someone publicly vouching for or defending you", tone: "good", chip: "bg-teal-100 text-teal-800 dark:bg-teal-500/15 dark:text-teal-300", dot: "bg-teal-500" },
};

/** The ones that need a human today. Everything else is context. */
export const URGENT_SIGNALS = ["legal_threat", "allegation", "boycott", "misinformation", "impersonation"];

export const signalMeta = (key) => SIGNAL_META[key] || { label: key, short: key, hint: "", tone: "watch", chip: "bg-muted text-muted-foreground", dot: "bg-input" };

/** Signals on a reply, ordered as the threat lane shows them. */
export const orderSignals = (list = []) => SIGNALS.filter((s) => list.includes(s));

/** for → positive, against → negative, none → neutral. */
export const stanceToPolarity = (s) => (s === "for" ? "positive" : s === "against" ? "negative" : "neutral");
